import styled from "@emotion/styled";
import { AnimatePresence, LayoutGroup, m, useIsPresent } from "framer-motion";
import React, { forwardRef } from "react";

import type { INavMenu } from "../../types";

import { NavGroup } from "./NavGroup";

const SubmenuContainer = styled(m.div)(({ theme }) => ({
  display: "grid",
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  width: "100%",
  maxWidth: `${theme.maxWidths.moduleGrid}`,
  background: theme.colors.background.primary,
  overflowY: "hidden",
  borderTop: `1px solid ${theme.colors.border.default}`,
}));

const InnerSubmenuContainer = styled(m.div)(({ theme }) => ({
  display: "grid",
  gridRow: 1,
  gridColumn: 1,
  width: "100%",
  color: theme.colors.content.primary,
  gridTemplateColumns: "repeat(4, 1fr)",
  maxWidth: `${theme.maxWidths.moduleGrid}`,
  margin: "0 auto",
  position: "relative",
  left: 0,
}));

interface Props {
  groups: INavMenu["submenu"];
  menu: string;
  darkModeNav?: boolean;
}

export const Submenu = forwardRef<HTMLDivElement, Props>(function Submenu(
  { groups, menu, darkModeNav },
  ref,
) {
  return (
    <SubmenuContainer
      ref={ref}
      layout="position"
      transition={{
        duration: 0.2,
        type: "spring",
        stiffness: 500,
        damping: 40,
      }}
    >
      <AnimatePresence mode="wait">
        <LayoutGroup>
          <InnerMenu key={menu} groups={groups} darkModeNav={darkModeNav} />
        </LayoutGroup>
      </AnimatePresence>
    </SubmenuContainer>
  );
});

function InnerMenu({
  groups,
  darkModeNav,
}: Pick<Props, "groups" | "darkModeNav">) {
  const isPresent = useIsPresent();
  const normalizedGroups = groups || [];
  return (
    <InnerSubmenuContainer
      data-present={isPresent}
      data-testid={isPresent ? "nav-submenu" : undefined}
      variants={{
        initial: { opacity: 0, x: -20 },
        animate: { opacity: 1, x: 0, zIndex: 0 },
        exit: { opacity: 0, x: 20, zIndex: -1 },
      }}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{
        duration: 0.2,
        ease: "easeInOut",
      }}
    >
      {normalizedGroups.map((group, i) =>
        group.hideOnDesktop ? null : (
          <NavGroup
            key={group._key}
            group={group}
            collapsePaddingRight={
              group.theme === normalizedGroups[i + 1]?.theme
            }
            collapsePaddingLeft={group.theme === normalizedGroups[i - 1]?.theme}
            darkModeNav={darkModeNav}
          />
        ),
      )}
    </InnerSubmenuContainer>
  );
}
